import React, { useCallback } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { Bell } from '@withjoy/joykit/icons';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestGuideTelemetry } from '../GuestGuideTelemetry';
import { useTranslation } from '@shared/core';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { STANDARD_TRANSITION } from '@shared/utils/animationTransition';
import { useCarbonIframeLinkProps } from '@shared/hooks/useCarbonIframeLinkProps';

type BookingCtaCardProps = Readonly<{
  countdownDays: number;
  isEventDay?: boolean;
}>;

export const BookingCtaCard: React.FC<BookingCtaCardProps> = ({ countdownDays, isEventDay }) => {
  const { eventHandle } = useEventInfo();
  const telemetry = useGuestGuideTelemetry();
  const { tq } = useTranslation('guestGuide');
  const hotelRoutePaths = useHotelRoutePaths();

  const handleClick = useCallback(() => {
    telemetry.clickBookingCta();
  }, [telemetry]);
  const carbonIframeLinkProps = useCarbonIframeLinkProps();

  return (
    <Box
      as="a"
      href={hotelRoutePaths.bookYourStay.buildRelativeUrl(eventHandle || '', {
        utm_campaign: 'guest_guide',
        utm_medium: 'web',
        utm_source: 'guest_guide_booking_cta',
      })}
      onClick={handleClick}
      {...carbonIframeLinkProps}
      display="flex"
      width="100%"
      gap={3}
      alignItems="center"
      paddingLeft={6}
      paddingRight={3}
      paddingY={4}
      borderRadius="12px"
      border="2px solid"
      borderColor="#4951EF"
      backgroundColor="white"
      cursor="pointer"
      transition={`all ${STANDARD_TRANSITION}`}
      _hover={{
        borderColor: '#3339CC',
      }}
      marginBottom={5}
      textDecoration="none"
    >
      <Bell
        size={32}
        color="#4951EF"
      />
      <Box flex={1}>
        <TextV2
          typographyVariant="label3"
          fontWeight="semibold"
          color="mono14"
        >
          {isEventDay
            ? tq`It's the big day!`
            : tq.plural(countdownDays, {
                one: tq`1 day to the big event!`,
                other: tq`${countdownDays} days to the big event!`,
              })}
        </TextV2>
        <TextV2
          typographyVariant="label2"
          color="mono14"
        >
          {isEventDay ? tq`Book your stay if you haven't already.` : tq`Book your stay, before rooms sell out.`}
        </TextV2>
      </Box>
    </Box>
  );
};
